import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Check, X, CalendarBlank, Trash } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api, { formatDate, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

const TYPES = ["Cuti Tahunan", "Sakit", "Izin", "Cuti Melahirkan", "Cuti Menikah", "Cuti Wafat"];

export default function Cuti() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ leave_type: "Cuti Tahunan", start_date: "", end_date: "", days: 1, reason: "" });
  const isSuperadmin = user?.role === "superadmin";
  const canApprove = isSuperadmin; // Hanya Super Admin yang bisa approve/reject
  const canSubmit = !isSuperadmin; // Super Admin tidak mengajukan cuti
  const canPickEmployee = ["admin", "hr"].includes(user?.role); // Admin Lembaga / HR mengajukan atas nama karyawan

  const load = async () => {
    const [l, e] = await Promise.all([api.get("/leaves"), api.get("/employees")]);
    setItems(l.data); setEmployees(e.data);
  };
  useEffect(() => { load(); }, []);

  const empMap = Object.fromEntries(employees.map((e) => [e.employee_id, e]));

  const submit = async () => {
    try {
      await api.post("/leaves", { ...form, days: Number(form.days || 1) });
      toast.success("Pengajuan cuti terkirim");
      setOpen(false); setForm({ leave_type: "Cuti Tahunan", start_date: "", end_date: "", days: 1, reason: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const act = async (id, status) => {
    const note = status === "rejected" ? prompt("Alasan penolakan (opsional):") : "";
    try { await api.put(`/leaves/${id}/action`, { status, note: note || null }); toast.success(status === "approved" ? "Disetujui" : "Ditolak"); load(); } catch { toast.error("Gagal"); }
  };
  const remove = async (id) => {
    if (!confirm("Hapus pengajuan?")) return;
    try { await api.delete(`/leaves/${id}`); load(); toast.success("Terhapus"); } catch { toast.error("Gagal"); }
  };

  return (
    <div data-testid="cuti-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Human Resource</div>
          <h1 className="font-display text-3xl font-black text-gray-900">Cuti & Izin Karyawan</h1>
          <p className="text-gray-600 text-sm mt-1">Kelola pengajuan cuti, izin, dan sakit.</p>
        </div>
        <Button onClick={()=>setOpen(true)} disabled={!canSubmit} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white disabled:opacity-50" data-testid="cuti-add-button" title={!canSubmit ? "Super Admin hanya melakukan approve/reject" : ""}><Plus size={16} className="mr-1.5" />Ajukan Cuti</Button>
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr><th className="px-4 py-3 font-semibold">Karyawan</th><th className="px-4 py-3 font-semibold">Jenis</th><th className="px-4 py-3 font-semibold">Tanggal</th><th className="px-4 py-3 font-semibold">Hari</th><th className="px-4 py-3 font-semibold">Alasan</th><th className="px-4 py-3 font-semibold">Status</th><th className="px-4 py-3 font-semibold text-right">Aksi</th></tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2.5">
                    <div className="font-semibold">{l.employee_name || empMap[l.employee_id]?.name || l.employee_id}</div>
                    <div className="text-xs text-gray-500 font-mono">{l.employee_id}</div>
                  </td>
                  <td className="px-4 py-2.5"><span className="inline-block px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-semibold">{l.leave_type}</span></td>
                  <td className="px-4 py-2.5 font-mono text-xs">{formatDate(l.start_date)} — {formatDate(l.end_date)}</td>
                  <td className="px-4 py-2.5 text-center font-mono">{l.days}</td>
                  <td className="px-4 py-2.5 max-w-xs truncate">{l.reason}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                      l.status === "approved" ? "bg-[#CCFBF1] text-[#0B8D95]" :
                      l.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                    }`}>{l.status}</span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {canApprove && l.status === "pending" && <>
                      <button onClick={()=>act(l.id, "approved")} className="p-1.5 hover:bg-green-50 rounded text-[#059669]" title="Setujui" data-testid={`cuti-approve-${l.id}`}><Check size={14} /></button>
                      <button onClick={()=>act(l.id, "rejected")} className="p-1.5 hover:bg-red-50 rounded text-red-600" title="Tolak" data-testid={`cuti-reject-${l.id}`}><X size={14} /></button>
                    </>}
                    <button onClick={()=>remove(l.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`cuti-delete-${l.id}`}><Trash size={14} /></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-gray-500">Belum ada pengajuan</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="cuti-dialog">
          <DialogHeader><DialogTitle>Ajukan Cuti / Izin</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {canPickEmployee && (
              <div><Label>Karyawan (yang mengajukan)</Label>
                <Select value={form.employee_id || "self"} onValueChange={(v)=>setForm({...form, employee_id: v === "self" ? undefined : v})}>
                  <SelectTrigger className="bg-white"><SelectValue placeholder="Diri sendiri" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    <SelectItem value="self">Diri sendiri</SelectItem>
                    {employees.map((e)=><SelectItem key={e.employee_id} value={e.employee_id}>{e.employee_id} - {e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Jenis Cuti *</Label>
              <Select value={form.leave_type} onValueChange={(v)=>setForm({...form, leave_type: v})}>
                <SelectTrigger className="bg-white" data-testid="cuti-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{TYPES.map((t)=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Mulai *</Label><Input type="date" value={form.start_date} onChange={(e)=>setForm({...form, start_date: e.target.value})} data-testid="cuti-start-input" /></div>
              <div><Label>Selesai *</Label><Input type="date" value={form.end_date} onChange={(e)=>setForm({...form, end_date: e.target.value})} /></div>
              <div><Label>Hari</Label><Input type="number" value={form.days} onChange={(e)=>setForm({...form, days: e.target.value})} /></div>
            </div>
            <div><Label>Alasan *</Label><Textarea value={form.reason} onChange={(e)=>setForm({...form, reason: e.target.value})} data-testid="cuti-reason-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button onClick={submit} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="cuti-submit-button">Ajukan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
